import "./reportAnon.css";

import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  Box,
  Button,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ClientConfig from "shared/config";
import AnonymImage from "shared/assets/anonym.webp";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import {
  getApiKeyHeaders,
  getAuthorizationHeaders,
} from "shared/common/utils/general-utils";
import PopoverElement from "shared/common/utils/popover/PopoverElement";
import UseAuth from "shared/common/hooks/UseAuth";
import { useSnackbar } from "shared/common/contexts/SnackbarProvider";

type LabelCreatePayload = {
  data_path: string;
  provided_by?: string;
  status: "suggested";
  frame_uid?: string | null;
  geom: {
    type: "Polygon";
    coordinates: number[][][];
  };
};

export default function ReportAnon() {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const viewer = defaultStore((state) => state.viewer);
  const reportAnonymization = defaultStore(
    (state) => state.reportAnonymization
  );
  const changeReportAnonymization = defaultStore(
    (state) => state.changeReportAnonymization
  );

  const [frameFeatureTool, setFrameFeatureTool] =
    useState<mm.FrameFeatureTool>();
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { userData, tokens } = UseAuth();
  const { showMessage } = useSnackbar();

  const { t } = useTranslation();

  //close report anon window
  const handleReportAnonymization = () => {
    changeReportAnonymization(!reportAnonymization);
  };

  const postLabel = async (
    payload: LabelCreatePayload,
    accessToken: string
  ) => {
    const response = await fetch(`${ClientConfig.riagateURL}labels`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthorizationHeaders(accessToken),
        ...getApiKeyHeaders(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Riagate request failed: ${response.status} ${response.statusText}`
      );
    }
  };

  //the tool keeps the drawing until it is reset, so the payloads are built from the current drawing on demand
  const buildPayloads = (): LabelCreatePayload[] => {
    const geojson = frameFeatureTool?.getDrawnFeatures();

    if (!geojson?.features?.length) return [];

    return geojson.features
      .map((feature: any): LabelCreatePayload | null => {
        if (
          feature?.geometry?.type !== "Polygon" ||
          !feature?.geometry?.coordinates
        ) {
          console.warn("Skipping invalid feature:", feature);
          return null;
        }

        return {
          frame_uid: feature?.properties?.frameId,
          data_path: feature?.properties?.dataPath,
          provided_by: userData?.tenant,
          status: "suggested",
          geom: feature?.geometry,
        };
      })
      .filter(
        (payload: LabelCreatePayload | null): payload is LabelCreatePayload =>
          payload !== null
      );
  };

  //add frame tool if viewer is defined
  useEffect(() => {
    if (viewer === undefined) return;

    const tool = viewer
      .getScene()
      .getToolHandler()
      .createFrameFeatureTool(
        {
          finishedcbf: () => setActiveStep(1),
          resetcbf: () => setActiveStep((step) => (step === 1 ? 0 : step)),
          //remove option to select or edit drawn features
          selectedcbf: (feature: any) => {
            if (tool.getSelectedFeatures().indexOf(feature) !== -1) {
              tool.selectFrameFeature(feature);
              viewer.getScene().infrascene.render();
            }
          },
          allowMultipleDraws: false,
          cleanupAfterFinish: false,
        },
        "ria"
      );

    setFrameFeatureTool(tool);
  }, [viewer]);

  //steps array
  const steps = [
    {
      label: t("ria.step_one_title"),
      description: t("ria.step_one_description"),
    },
    {
      label: t("ria.step_two_title"),
      description: t("ria.step_two_description"),
    },
    {
      label: t("ria.step_three_title"),
      description: t("ria.step_three_description"),
    },
  ];

  useEffect(() => {
    //reset state when closing the report dialog
    if (!reportAnonymization) setActiveStep(0);
  }, [reportAnonymization]);

  //step 0: draw, step 1: confirm the drawing, step 2: done
  useEffect(() => {
    if (frameFeatureTool === undefined) return;

    const keepToolActive =
      reportAnonymization && (activeStep === 0 || activeStep === 1);

    //enabling an already enabled tool resets it and would delete the drawing
    if (keepToolActive !== frameFeatureTool.enabled) {
      keepToolActive ? frameFeatureTool.enable() : frameFeatureTool.disable();
    }
  }, [activeStep, frameFeatureTool, reportAnonymization]);

  //drawing locks the navigation and the tool only releases it on reset
  useEffect(() => {
    if (activeStep === 1) viewer?.getScene().infrascene.unlockNavigation();
  }, [activeStep, viewer]);

  //close report anon window
  const handleCloseEvents = () => {
    setActiveStep(0);
    handleReportAnonymization();
  };

  //reset active step to report another object
  const handleSelectAnother = () => {
    setActiveStep(0);
  };

  //discard changes
  const no = () => {
    frameFeatureTool?.reset();
    setActiveStep(0);
  };

  //save changes
  const yes = async () => {
    const payloads = buildPayloads();
    if (!payloads.length) {
      showMessage(t("ria.error_invalid_features"), "error");
      return;
    }

    const accessToken = tokens?.access_token;
    if (!accessToken) {
      showMessage(t("ria.error_unexpected"), "error");
      return;
    }

    setIsSubmitting(true);

    try {
      for (const payload of payloads) {
        await postLabel(payload, accessToken);
      }

      setActiveStep(2);
    } catch (saveError) {
      showMessage(t("ria.error_unexpected") + " (" + saveError + ")", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PopoverElement
      maximizeHeight={true}
      visible={reportAnonymization}
      handleClose={handleReportAnonymization}
      title={t("ria.title")}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          height: "100%",
          color: darkTheme ? "white" : "darkblue.main",
        }}
      >
        <img width={"100%"} src={AnonymImage} />
        <Typography variant="body2">{t("ria.message")}</Typography>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel
                classes={{
                  label: darkTheme
                    ? "infra3d-stepLabelDark"
                    : "infra3d-stepLabelLight",
                }}
              >
                {step.label}
              </StepLabel>
              <StepContent>
                <Typography variant="body2">{step.description}</Typography>
                <Box sx={{ mb: 2 }}>
                  {activeStep === 1 && (
                    <LoadingButton
                      variant="contained"
                      size="medium"
                      sx={{ mt: 1, mr: 1 }}
                      loading={isSubmitting}
                      onClick={() => monitor(apm, "clicked-yes | RIA", yes)}
                    >
                      {t("ria.stepper_yes")}
                    </LoadingButton>
                  )}
                  {activeStep === 1 && (
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ mt: 1, mr: 1 }}
                      disabled={isSubmitting}
                      onClick={() => monitor(apm, "clicked-no | RIA", no)}
                    >
                      {t("ria.stepper_no")}
                    </Button>
                  )}
                  {activeStep === 2 && (
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ mt: 1, mr: 1 }}
                      disabled={isSubmitting}
                      onClick={() =>
                        monitor(
                          apm,
                          "select-another-object | RIA",
                          handleSelectAnother
                        )
                      }
                    >
                      {t("ria.stepper_select_another_object")}
                    </Button>
                  )}
                  {activeStep === 2 && (
                    <Button
                      variant="contained"
                      size="medium"
                      sx={{ mt: 1, mr: 1 }}
                      disabled={isSubmitting}
                      onClick={() =>
                        monitor(apm, "clicked-close | RIA", handleCloseEvents)
                      }
                    >
                      {t("ria.stepper_close")}
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>
    </PopoverElement>
  );
}
